import { useT } from '@/core/providers/I18nProvider';
import { useBillingTier } from '@/hooks/useBillingTier';
import { LEGAL_LINKS } from '@/lib/legal-links';
import { SUPPORT_MAILTO, supportSlaKey, TROUBLESHOOTING_DOCS_URL } from '@/lib/support-links';

import type { ReactElement } from 'react';

/**
 * Slim site-wide legal footer for the authenticated dashboard. The legal
 * documents are published on the marketing origin, so these open there in a
 * new tab (keeping the dashboard session intact). Rendered by AppShell at the
 * bottom of every page so Terms / Privacy / Cookies / DPA / AI disclosure are
 * always one click away — a transparency requirement, not decoration.
 *
 * A19 adds the support row: the canonical support address + the docs
 * troubleshooting guide + the workspace's tier-appropriate SLA line (rendered
 * only once the tier read RESOLVES — an unknown tier shows links, no promise;
 * never a wrong SLA at a paying customer).
 */
export function LegalFooter(): ReactElement {
  const { t } = useT();
  const { tier } = useBillingTier();
  const year = new Date().getFullYear();
  return (
    <footer
      aria-label={t('footer.legalLabel')}
      className="mt-auto border-t border-hairline px-6 py-4 text-xs text-muted-foreground"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span>© {year} Syncanix</span>
        <nav
          aria-label={t('footer.legalLinksLabel')}
          className="flex flex-wrap items-center gap-x-4 gap-y-2"
        >
          {LEGAL_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="underline-offset-2 hover:text-foreground hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {t(link.labelKey)}
            </a>
          ))}
        </nav>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
        <nav
          aria-label={t('support.linksLabel')}
          className="flex flex-wrap items-center gap-x-4 gap-y-2"
        >
          <a
            href={SUPPORT_MAILTO}
            className="underline-offset-2 hover:text-foreground hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {t('support.contact')}
          </a>
          <a
            href={TROUBLESHOOTING_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="underline-offset-2 hover:text-foreground hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {t('support.docs')}
          </a>
        </nav>
        {tier !== undefined ? <span>{t(supportSlaKey(tier))}</span> : null}
      </div>
    </footer>
  );
}
